import axios, {registerTokenRefresher} from './axiosInstance';
import {getTokenId, isScopeMatched, isTokenExpired, SCOPE_ADMIN} from './tokenClaims';

/**
 * [AT 수명 24시간에 맞춘 갱신주기]
 *  예전에는 AT 가 1시간이라 '50분 경과 시 갱신 + 5분마다 점검' 이었다. 그래서 활성
 *  사용자는 약 55분마다 재발급을 했고, 그 빈도 때문에 기기를 번갈아 쓸 때 재발급이
 *  자주 충돌했다(서버가 계정당 RT 를 1개만 보관하고 매번 교체했기 때문).
 *  서버에서 RT 를 기기별로 보관하도록 고치고 AT 를 24시간으로 늘렸으므로,
 *  갱신도 그에 맞춰 늦춘다. (20시간 + 30분 점검 < 24시간)
 */

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  관리자 토큰 관리
 * ════════════════════════════════════════════════════════════════════════════════
 *  [관리자는 localStorage 만 쓴다]
 *   일반 사용자는 '로그인 유지' 여부에 따라 localStorage / sessionStorage 를 나눠 쓰지만,
 *   관리자는 언제나 localStorage 에만 저장한다.
 *     adminToken          : 액세스토큰(AT)
 *     adminLastLoginTime  : 마지막으로 토큰을 받은 시각 (ISO 문자열)
 *
 *  [왜 이 모듈이 생겼나]
 *   관리자 토큰이 재발급된 적이 한 번도 없었다.
 *   App.js 의 관리자 타이머가 'adminLastLoginTime' 이 아니라
 *   일반 사용자 키인 'lastLoginTime' 을 읽고 있었기 때문이다.
 *   관리자만 로그인한 브라우저에는 그 키가 없으므로 타이머가 항상 조기 종료되었고,
 *   AT(1시간)가 만료된 뒤 모든 API 가 401 이 되어 로그인 화면으로 튕겼다.
 *
 *  [시간값]
 *   AT 유효기간이 1시간(application.yml access-token-validity-in-seconds: 3600)이므로
 *   만료 전에 미리 갱신한다. 1시간 주기로 점검하면 이미 만료된 뒤에 돌게 된다.
 *
 *  [서버측 전제]
 *   /member/reissue 는 만료된 AT 도 받아준다. (JwtTokenProvider.getClaims 가
 *   ExpiredJwtException 을 잡아 클레임을 돌려주므로)
 *   대신 httpOnly 쿠키 refresh-token 과 Redis 의 RT 가 필요하므로
 *   반드시 withCredentials 로 보내야 한다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

export const ADMIN_AT_KEY = 'adminToken';
export const ADMIN_LOGIN_TIME_KEY = 'adminLastLoginTime';

/** AT 는 24시간이므로 20시간이 지나면 미리 갱신한다 */
export const ADMIN_REISSUE_AFTER_MS = 20 * 60 * 60 * 1000;
/** 점검 주기 — 짧게 돌아도 실제 재발급은 위 조건을 넘길 때만 한다 */
export const ADMIN_CHECK_INTERVAL_MS = 30 * 60 * 1000;
/** RT 유효기간(30일)을 넘기면 재발급이 불가능하므로 정리한다 */
export const ADMIN_MAX_SESSION_MS = 30 * 24 * 60 * 60 * 1000;

export const getAdminToken = () => {
    try {
        return window.localStorage.getItem(ADMIN_AT_KEY);
    } catch (e) {
        return null;
    }
};

/**
 * 마지막으로 토큰을 받은 시각
 *  로그인(AdminLoginPg)은 ISO 문자열로 저장하지만, 예전 재발급 코드가
 *  Date 객체를 그대로 넣어 브라우저 기본 문자열이 들어간 경우도 있다.
 *  둘 다 받아들이고, 해석할 수 없으면 null 로 본다.
 */
export const getAdminLoginTime = () => {
    try {
        const raw = window.localStorage.getItem(ADMIN_LOGIN_TIME_KEY);
        if (!raw) return null;
        const time = new Date(raw).getTime();
        return Number.isFinite(time) ? time : null;
    } catch (e) {
        return null;
    }
};

const setAdminLoginTime = (date) => {
    // 저장형식을 ISO 로 통일한다 (브라우저 기본 문자열은 환경에 따라 해석이 달라진다)
    window.localStorage.setItem(ADMIN_LOGIN_TIME_KEY, date.toISOString());
};

/** 관리자 세션 정리 (토큰 + 시각) */
export const clearAdminSession = () => {
    try {
        window.localStorage.removeItem(ADMIN_AT_KEY);
        window.localStorage.removeItem(ADMIN_LOGIN_TIME_KEY);
    } catch (e) { /* 저장소 접근 실패는 무시 */ }
    /*
     * 재시도 차단 플래그를 함께 푼다.
     *  쿠키 미전달(400)로 한 번 막히면 그 뒤로는 영영 재발급을 시도하지 않았다.
     *  세션을 정리했다는 건 '다음에 새로 로그인한다' 는 뜻이므로 여기서 풀어준다.
     *  (풀지 않으면 다시 로그인해도 같은 탭에서는 재발급이 영구히 막힌 채였다)
     */
    reissueDisabled = false;
};

/** 같은 순간에 재발급 요청이 여러 번 나가지 않도록 하나로 묶는다 (이 탭 안에서) */
let inFlight = null;

/*
 * ════════════════════════════════════════════════════════════════════════════════
 *  탭 사이의 재발급 중복을 막는 잠금
 * ════════════════════════════════════════════════════════════════════════════════
 *  서버는 재발급 때마다 RT 를 교체하고(MemberService.reissue),
 *  '요청된 RT 가 Redis 의 RT 와 다르면' 탈취로 간주해 Redis 의 RT 를 지운다.
 *  그래서 관리자 화면을 여러 탭에 띄워두면
 *    탭A 재발급 성공 -> RT 교체
 *    탭B 가 옛 쿠키로 도착 -> 불일치 -> 서버가 RT 삭제
 *  가 되어 정상 세션이 죽고 이후 모든 재발급이 401 이 된다.
 *  inFlight 는 같은 탭 안에서만 유효하므로 localStorage 로 짧은 잠금을 건다.
 *  (사용자용과 키를 다르게 둔다 — 서로를 막아서는 안 된다)
 * ════════════════════════════════════════════════════════════════════════════════
 */
const REISSUE_LOCK_KEY = 'adminReissueLock';
const REISSUE_LOCK_TTL_MS = 15 * 1000;

/** @returns {boolean} 내가 재발급을 맡았는지 */
const acquireReissueLock = () => {
    try {
        const raw = window.localStorage.getItem(REISSUE_LOCK_KEY);
        const now = Date.now();
        if (raw) {
            const at = Number(raw);
            if (Number.isFinite(at) && at > 0 && now - at < REISSUE_LOCK_TTL_MS) return false;
        }
        window.localStorage.setItem(REISSUE_LOCK_KEY, String(now));
        return true;
    } catch (e) {
        return true;
    }
};

const releaseReissueLock = () => {
    try {
        window.localStorage.removeItem(REISSUE_LOCK_KEY);
    } catch (e) { /* 무시 */ }
};

/**
 * 서버가 '이 세션은 끝났다' 고 알려준 응답인지 판별한다.
 *  401 : Redis 에 RT 가 없거나 불일치 — 서버가 재발급을 거부했다
 *  403 : 권한 거부
 *
 *  [400 은 여기 넣지 않는다 — 운영 장애에서 배운 점]
 *   400 은 refresh-token 쿠키가 요청에 실리지 않았을 때 나온다(@CookieValue 필수).
 *   '세션이 끝났다' 가 아니라 '쿠키가 전달되지 않았다' 는 환경 문제이므로
 *   강제 로그아웃시키지 않고 아래 reissueDisabled 로 재시도만 멈춘다.
 */
const isSessionEnded = (status) => status === 401 || status === 403;

/*
 * 쿠키가 없어 재발급이 불가능한 환경이면, 이 페이지 세션 동안은 더 시도하지 않는다.
 * 새로고침하면 초기화된다.
 */
let reissueDisabled = false;

/**
 * 액세스토큰 재발급
 *
 *  @returns {Promise<boolean>} 성공여부
 *
 *  [응답 검증]
 *   Authorization 응답헤더가 비어 있으면 실패로 본다.
 *   예전에는 검증 없이 저장해서 localStorage 에 "null" 이라는 문자열이 들어갔고,
 *   그 값은 truthy 라 라우트 가드를 통과해버려 '토큰은 있는데 전부 401' 인
 *   상태가 계속되었다.
 */
export const reissueAdminToken = () => {
    if (inFlight) return inFlight;

    // 쿠키가 없어 재발급이 불가능한 환경이면 반복 호출하지 않는다 (로그아웃도 하지 않는다)
    if (reissueDisabled) return Promise.resolve(false);

    // 재발급 시점의 최신 토큰을 읽는다 (닫힌 변수에 갇힌 옛 토큰을 쓰지 않도록)
    const currentToken = getAdminToken();
    if (!currentToken) return Promise.resolve(false);

    /*
     * 다른 탭이 재발급 중이면 나는 보내지 않는다.
     * 그 탭이 성공하면 localStorage 에 새 토큰이 들어오므로 다음 점검에서 그 값을 쓴다.
     * (여기서 같이 보내면 옛 쿠키로 도착해 서버가 RT 를 지워버린다)
     */
    if (!acquireReissueLock()) {
        console.log('[AdminAuth] 다른 탭이 재발급 중이라 건너뜁니다.');
        return Promise.resolve(true);
    }

    inFlight = axios({
        method: 'post',
        /*
         * [중요] 관리자 전용 창구를 쓴다.
         *  예전에는 사용자와 같은 '/member/reissue' 를 썼다. 그런데 브라우저의 RT 쿠키는
         *  이름이 하나뿐이어서, 사용자로 로그인하는 순간 관리자 쿠키가 덮어써졌다.
         *  그 상태로 재발급하면 서버가 RT(=사용자 것)로 신원을 정해 '사용자 토큰' 을
         *  내려주었고, 아래에서 그것을 관리자 토큰 자리에 저장해 관리자 화면이
         *  사용자 권한으로 동작했다. (운영에서 실제로 발생한 사고)
         *  이제 쿠키와 창구를 영역별로 분리했으므로 관리자 창구는 admin 영역 RT 만 받는다.
         */
        url: '/member/adminReissue',
        headers: {Authorization: currentToken},
        withCredentials: true,   // httpOnly 쿠키(__Host-rt_admin)를 함께 보내야 한다
    })
        .then((response) => {
            const headers = response?.headers;
            const issued = headers
                ? (typeof headers.get === 'function'
                    ? headers.get('Authorization') || headers.get('authorization')
                    : headers.Authorization || headers.authorization)
                : null;

            if (!issued || issued === 'null' || issued === 'undefined') {
                console.log('[AdminAuth] 재발급 응답에 토큰이 없습니다.');
                return false;
            }

            /*
             * [2차 방어] 받은 토큰이 정말 '관리자 영역' 토큰인지, 그리고 '같은 계정' 인지
             *  저장하기 전에 확인한다. 서버가 이미 영역을 검사하지만, 서버가 구버전이거나
             *  중간에 응답이 바뀌는 상황에서도 관리자 자리에 사용자 토큰이 들어앉는 일은
             *  절대 없어야 한다. 어긋나면 저장하지 않고 세션을 정리해 로그인으로 보낸다.
             */
            if (!isScopeMatched(issued, SCOPE_ADMIN)) {
                console.log('[AdminAuth] 관리자 영역 토큰이 아닙니다 — 세션을 정리합니다.');
                clearAdminSession();
                return false;
            }
            const previousId = getTokenId(currentToken);
            const issuedId = getTokenId(issued);
            if (previousId && issuedId && previousId !== issuedId) {
                console.log('[AdminAuth] 재발급된 토큰의 계정이 다릅니다 — 세션을 정리합니다.');
                clearAdminSession();
                return false;
            }

            window.localStorage.setItem(ADMIN_AT_KEY, issued);
            setAdminLoginTime(new Date());
            console.log('[AdminAuth] 토큰 재발급 성공');
            return true;
        })
        .catch((error) => {
            const status = error?.response?.status;

            /*
             * [여기가 운영에서 401 이 끝없이 반복된 원인이었다]
             *  예전에는 실패해도 토큰을 지우지 않았다. 그래서 되살릴 수 없는 실패(401)에도
             *  죽은 토큰이 남아 5분마다 /member/reissue 를 다시 때렸고, 서버는 이미
             *  RT 를 지운 상태라 영원히 401 이었다.
             *
             *  401 은 'Redis 에 RT 가 없거나 불일치' 일 때만 나오고, 불일치면 서버가
             *  RT 를 삭제한다. 즉 401 을 받은 순간 복구가 불가하다. 토큰을 남겨두면
             *  라우트 가드는 통과하는데 모든 API 가 401 인 더 나쁜 상태가 되므로 정리한다.
             */
            if (isSessionEnded(status)) {
                console.log('[AdminAuth] 세션이 만료되어 정리합니다. (' + status + ')');
                clearAdminSession();
                return false;
            }

            /*
             * 400 — 쿠키가 전달되지 않았다. 세션은 건드리지 않고 재시도만 멈춘다.
             * 액세스토큰이 살아 있는 동안은 그대로 사용하고, 만료되면 실제 API 401 에서
             * 각 화면이 로그인으로 유도한다. (작업중 강제 로그아웃시키지 않는다)
             */
            if (status === 400) {
                reissueDisabled = true;
                console.log('[AdminAuth] 재발급 쿠키가 없어 재시도를 중단합니다. (400)');
                return false;
            }

            // 네트워크 끊김 / 5xx / 타임아웃 — 일시적 장애다. 토큰을 지우지 않고 다음 점검에서 재시도한다
            console.log('[AdminAuth] 토큰 재발급 일시실패 — 다음 점검에서 재시도', status || error?.message || '');
            return false;
        })
        .finally(() => {
            inFlight = null;
            releaseReissueLock();
        });

    return inFlight;
};

/**
 * 필요하면 재발급한다.
 *  @param force true 면 경과시간과 무관하게 즉시 재발급
 *  @returns {Promise<boolean>} 세션이 유효한 상태로 끝났는지
 */
export const ensureAdminToken = async (force = false) => {
    if (!getAdminToken()) return false;

    const loginTime = getAdminLoginTime();

    /*
     * 시각 정보가 없으면(예전 로그인 / 저장 실패) 판단할 수 없다.
     * 세션을 끊지 않고 한 번 갱신해 시각을 다시 심는다.
     */
    if (loginTime === null) return reissueAdminToken();

    const elapsed = Date.now() - loginTime;

    // RT 유효기간을 넘겼으면 재발급이 불가능하다
    if (elapsed >= ADMIN_MAX_SESSION_MS) {
        console.log('[AdminAuth] 로그인 후 30일이 지나 세션을 정리합니다.');
        clearAdminSession();
        return false;
    }

    if (force || elapsed >= ADMIN_REISSUE_AFTER_MS) {
        return reissueAdminToken();
    }
    return true;   // 아직 유효 — 아무것도 하지 않는다
};

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  관리자 화면에 들어가도 되는지 판단한다 (라우트 가드용)
 * ════════════════════════════════════════════════════════════════════════════════
 *  [예전 가드의 문제]
 *   localStorage 에 'adminToken' 이 '있기만' 하면 통과시켰다. 그래서
 *     · 만료된 토큰이어도 통과   -> 화면은 열리는데 모든 API 가 401
 *     · 사용자 토큰이 들어있어도 통과 -> 관리자 화면이 사용자 권한으로 동작 (실제 사고)
 *   둘 다 '로그아웃된 줄 모르고 쓰다가' 사고로 이어졌다.
 *
 *  [지금]
 *   ① 영역이 admin 인가  ② 만료되지 않았는가 를 본다.
 *   만료만 됐다면 조용히 재발급해서 통과시킨다(사용자는 만료를 체감하지 않는다).
 *   재발급까지 실패하면 세션을 정리하고 false -> 가드가 로그인 화면으로 보낸다.
 *
 *  @returns {Promise<boolean>} true = 관리자 화면 진입 허용
 * ════════════════════════════════════════════════════════════════════════════════
 */
export const resolveAdminAccess = async () => {
    const token = getAdminToken();
    if (!token || token === 'null' || token === 'undefined') return false;

    /*
     * 관리자 자리에 관리자 토큰이 아닌 것이 들어있다 — 즉시 정리한다.
     * (쿠키가 섞여 사용자 토큰을 받아 저장했던 사고의 마지막 안전망)
     */
    if (!isScopeMatched(token, SCOPE_ADMIN)) {
        console.log('[AdminAuth] 관리자 토큰이 아닙니다 — 세션을 정리합니다.');
        clearAdminSession();
        return false;
    }

    if (!isTokenExpired(token)) return true;

    // 만료됐다 — 조용히 갱신해 본다
    const renewed = await reissueAdminToken();
    if (!renewed) {
        clearAdminSession();
        return false;
    }

    /*
     * 다른 탭이 갱신 중이면 reissueAdminToken 이 true 를 돌려주지만 이 탭의
     * localStorage 에는 아직 새 토큰이 안 들어와 있을 수 있다. 잠깐 기다려 본다.
     * (여기서 바로 실패로 처리하면 탭 두 개를 띄워둔 것만으로 로그아웃된다)
     */
    for (let i = 0; i < 20; i += 1) {
        const fresh = getAdminToken();
        if (fresh && isScopeMatched(fresh, SCOPE_ADMIN) && !isTokenExpired(fresh)) return true;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 150));
    }

    clearAdminSession();
    return false;
};

/**
 * 관리자 화면에서 API 가 실패했을 때 로그인 화면으로 보낼지 판단한다.
 *
 *  예전에는 오류 종류를 가리지 않고 바로 로그인 화면으로 보냈다.
 *  그래서 네트워크가 잠깐 끊기거나 서버가 500 을 돌려주기만 해도
 *  작업중인 화면에서 튕겨나갔다.
 *
 *  인증문제(401/403)일 때만, 그것도 재발급을 한 번 시도해본 뒤에 판단한다.
 *
 *  @returns {Promise<boolean>} true 면 로그인 화면으로 보내야 한다
 */
export const shouldSendAdminToLogin = async (error) => {
    const status = error?.response?.status;

    // 인증문제가 아니면 화면을 유지한다 (네트워크 순단 / 서버오류 등)
    if (status !== 401 && status !== 403) return false;

    // 토큰이 아예 없으면 재발급할 것도 없다
    if (!getAdminToken()) return true;

    const renewed = await reissueAdminToken();
    if (renewed) {
        console.log('[AdminAuth] 재발급에 성공했습니다. 화면을 유지합니다.');
        return false;
    }

    clearAdminSession();
    return true;
};

/*
 * ════════════════════════════════════════════════════════════════════════════════
 *  401 자동갱신 등록
 * ════════════════════════════════════════════════════════════════════════════════
 *  액세스토큰이 만료된 상태로 API 를 부르면 서버가 401 을 돌려준다.
 *  그때 axiosInstance 가 여기 등록된 함수로 토큰을 갱신하고 원래 요청을 다시 보낸다.
 *  타이머가 놓친 구간(탭 절전 / 기기 종료 / 시계 변경)을 메워서,
 *  리프레시토큰이 살아있는 동안에는 사용자가 만료를 체감하지 못하게 한다.
 *
 *  갱신 자체는 inFlight + 탭간 잠금으로 묶여 있으므로, 401 이 동시에 여러 개
 *  떠도 재발급 요청은 한 번만 나간다.
 * ════════════════════════════════════════════════════════════════════════════════
 */
// axiosInstance 를 모킹한 테스트처럼 이 함수가 없는 환경에서도 모듈이 깨지지 않게 방어한다
if (typeof registerTokenRefresher === 'function') {
    registerTokenRefresher('admin', ensureAdminToken, getAdminToken);
}
